import { Outlet } from 'react-router-dom'
import { useEffect, useState } from 'react'
import Sidebar from './Sidebar'
import PageTransition from './PageTransition'
import { getDataAPI, getEffectiveUserId } from '../lib/api'
import { makeCacheKey, writeSessionCache } from '../lib/sessionCache'

export default function Layout() {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed')
    return saved === 'true'
  })

  useEffect(() => {
    // Background prefetch so page navigation never blocks on "Loading…" screens.
    const run = async () => {
      const userId = getEffectiveUserId() || 'self'
      const cache = (endpoint: string, data: unknown) => {
        writeSessionCache(makeCacheKey([userId, 'api', endpoint]), data)
      }

      const safeGet = async (endpoint: string) => {
        try {
          const data = await getDataAPI(endpoint)
          cache(endpoint, data)
        } catch {
          // ignore
        }
      }

      // Core navigation surfaces
      await Promise.all([
        safeGet('user/getBookings'),
        safeGet('tasks'),
        safeGet('orders'),
        safeGet('financials'),
        safeGet('user/getCatalog'),
        safeGet('user/connectors/googleCalendar/status'),
        safeGet('user/connectors/googleCalendar/events?maxResults=50'),
        safeGet('user/connectors/googleCalendar/events?maxResults=250'),
      ])
    }

    // Defer to avoid competing with initial route fetches
    const t = window.setTimeout(() => void run(), 250)
    return () => window.clearTimeout(t)
  }, [])

  const handleToggleCollapse = () => {
    const newState = !isCollapsed
    setIsCollapsed(newState)
    localStorage.setItem('sidebarCollapsed', String(newState))
  }

  return (
    <div className="flex min-h-screen bg-transparent">
      <Sidebar isCollapsed={isCollapsed} onToggleCollapse={handleToggleCollapse} />
      <main className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-transparent ${isCollapsed ? 'ml-20' : 'ml-72'} transition-all duration-200`}>
        <div className="min-h-screen">
          <PageTransition>
            <Outlet />
          </PageTransition>
        </div>
      </main>
    </div>
  )
}
